/**
 * <card-video> — a product card's video, played only while its slide is shown.
 *
 * The PLP card (`snippets/product-card-stacked.liquid`) is a two-slide Horizon slideshow: the
 * featured image, then media 2. When media 2 is a Shopify-hosted video, its slide carries this
 * element with the raw `media | json` blob and nothing else: no <video>, no request, no decoder,
 * no `src` picked in Liquid. The slide's still frame is what shoppers see until the video plays.
 *
 * `parseSources` / `pickRendition` / `toStorefrontUrl` repeat the pdp-video versions on purpose:
 * the push-staging flow uploads this file as-is to `assets/`, with no bundler to resolve a cross-
 * module import. Keep them in sync when either file changes.
 *
 * The slide is shown by hover on desktop (Horizon's `product-card/previewImage`) and by swipe,
 * arrows or dots anywhere. All of those scroll Horizon's slide strip, so one signal covers them:
 * an IntersectionObserver on this element with the strip as root. Horizon's own markers do not:
 * `aria-hidden` is only updated by select(), not by a swipe.
 *
 * Rules, all for performance:
 *   1. Nothing until intent. The <video> is created on first show, after a short dwell, so a
 *      pointer sweeping across the grid downloads nothing.
 *   2. Shown means both in the slide strip and on the page: a card scrolled off screen with its
 *      video slide still selected stops too.
 *   3. One at a time. Starting a card stops the previous one.
 *   4. Leaving stops the download. A video not fully buffered loses its `src` (which cancels the
 *      request and frees the decoder); a fully buffered one is kept paused for an instant replay,
 *      at most MAX_KEPT of them, oldest dropped first.
 *   5. Off for reduced motion, Save-Data and slow connections: the still frame stays.
 *   6. A rejected play() (iOS Low Power Mode) quietly leaves the still frame.
 *
 * No play/pause button: playback only ever starts from the shopper's own hover or swipe and
 * stops when they move on, which is the pause mechanism WCAG 2.2.2 asks for.
 *
 * Plain element, no Horizon imports (theme-delta/README.md).
 */

/** Long enough to skip a pointer passing over a card, short enough not to feel late. */
export const DWELL_MS = 120;

/** Fully buffered card videos kept for instant replay. */
export const MAX_KEPT = 3;

/** Tallest rendition on a card: 480p, same ceiling as the PDP uses on phones. */
export const MAX_HEIGHT = 480;

const REDUCED_MOTION = "(prefers-reduced-motion: reduce)";
const SLOW_CONNECTIONS = new Set(["slow-2g", "2g", "3g"]);

/**
 * The `sources` of a `media | json` blob, or [] if it is missing or unreadable.
 *
 * @param {string | undefined} json
 * @returns {Array<{format?: string, height?: number, url?: string}>}
 */
export function parseSources(json) {
  if (!json) return [];
  try {
    const sources = JSON.parse(json)?.sources;
    return Array.isArray(sources) ? sources : [];
  } catch {
    return [];
  }
}

/**
 * The largest MP4 no taller than `maxHeight`; failing that, the smallest MP4; failing that, "".
 *
 * @param {Array<{format?: string, height?: number, url?: string}>} sources
 * @param {number} maxHeight
 * @returns {string}
 */
export function pickRendition(sources, maxHeight) {
  const mp4s = sources
    .filter((source) => source?.format === "mp4" && source.url)
    .map((source) => ({ url: source.url, height: Number(source.height) || 0 }))
    .sort((first, second) => first.height - second.height);
  if (mp4s.length === 0) return "";

  const fitting = mp4s.filter((source) => source.height <= maxHeight);
  return (fitting.length > 0 ? fitting[fitting.length - 1] : mp4s[0]).url;
}

/**
 * Rewrite Shopify's cdn.shopify.com video URL to the storefront's own /cdn/shop/videos/, so a
 * card and the PDP play from one origin over the connection the page already has open.
 *
 * @param {string | undefined} url
 * @returns {string}
 */
export function toStorefrontUrl(url) {
  if (!url) return "";
  return url.replace(
    /^https?:\/\/cdn\.shopify\.com\/videos\//,
    "/cdn/shop/videos/",
  );
}

/** Starts as soon as a sliver of the slide is in the strip, so a swipe gets a head start. */
const STRIP_THRESHOLD = 0.1;

/**
 * Whether an observed element shows at least `minRatio` of itself in the root. `isIntersecting`
 * alone is also true for a zero-area, edge-to-edge touch, which is exactly where the video slide
 * sits after swiping back to the first image: it would keep playing out of sight.
 *
 * @param {{isIntersecting: boolean, intersectionRatio: number}} entry
 * @param {number} [minRatio]
 * @returns {boolean}
 */
export function shown(entry, minRatio = 0) {
  if (!entry?.isIntersecting || !(entry.intersectionRatio > 0)) return false;
  return entry.intersectionRatio >= minRatio;
}

/**
 * Edge, not level: what to do when the combined "shown" state is recomputed. Only a change acts.
 * Acting on the level would restart a card another card had just stopped, on any later observer
 * callback for it (a threshold crossing while it scrolls away), and that restart would in turn
 * stop the card the shopper is actually looking at.
 *
 * @param {boolean} wasShown
 * @param {boolean} isShown
 * @returns {"show" | "hide" | "none"}
 */
export function transition(wasShown, isShown) {
  if (wasShown === isShown) return "none";
  return isShown ? "show" : "hide";
}

/**
 * Whether card videos may play at all on this device and connection.
 *
 * @param {{reducedMotion: boolean, saveData?: boolean, effectiveType?: string}} args
 * @returns {boolean}
 */
export function cardVideoAllowed({ reducedMotion, saveData, effectiveType }) {
  if (reducedMotion || saveData) return false;
  return !SLOW_CONNECTIONS.has(effectiveType ?? "");
}

/**
 * Whether a stopped video is worth keeping: only when the whole clip is already buffered, so
 * keeping it costs memory but never more network.
 *
 * @param {{duration: number, bufferedEnd: number}} args
 * @returns {boolean}
 */
export function fullyBuffered({ duration, bufferedEnd }) {
  if (!(duration > 0) || !Number.isFinite(duration)) return false;
  return bufferedEnd >= duration - 0.1;
}

/**
 * Least recently used first: which kept items to drop to stay within `max`.
 *
 * @template T
 * @param {T[]} kept oldest first
 * @param {number} max
 * @returns {T[]}
 */
export function overflow(kept, max) {
  return kept.length > max ? kept.slice(0, kept.length - max) : [];
}

/**
 * `HTMLElement` does not exist under `node --test`, and `class X extends undefined` throws at
 * definition time — which would make the pure helpers above untestable. Extending a plain class
 * in Node keeps the module importable; nothing instantiates it there.
 */
const ElementBase =
  typeof HTMLElement === "undefined"
    ? /** @type {any} */ (class {})
    : HTMLElement;

/** The card currently playing or about to. */
let active = /** @type {CardVideo | null} */ (null);

/** Stopped, fully buffered cards, oldest first. */
let kept = /** @type {CardVideo[]} */ ([]);

/**
 * Grebban's eslint (`consistent-this`) rejects `active = this`; passing it through a function is
 * the same assignment without the alias.
 * @param {CardVideo | null} card
 */
function setActive(card) {
  active = card;
}

/** One page-viewport observer for every card. */
let viewportObserver = /** @type {IntersectionObserver | null} */ (null);

function getViewportObserver() {
  if (!viewportObserver) {
    viewportObserver = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          /** @type {CardVideo} */ (entry.target).onViewport(shown(entry));
        }
      },
      { threshold: [0, 0.01] },
    );
  }
  return viewportObserver;
}

export class CardVideo extends ElementBase {
  #stripObserver = /** @type {IntersectionObserver | null} */ (null);
  #video = /** @type {HTMLVideoElement | null} */ (null);
  #dwell = /** @type {ReturnType<typeof setTimeout> | null} */ (null);
  #inStrip = false;
  #onPage = false;
  #shown = false;

  connectedCallback() {
    // Still set up: this was a move, not a removal (see disconnectedCallback).
    if (this.#stripObserver) return;
    if (typeof IntersectionObserver !== "function") return;

    const allowed = cardVideoAllowed({
      reducedMotion: matchMedia(REDUCED_MOTION).matches,
      saveData: Boolean(/** @type {any} */ (navigator).connection?.saveData),
      effectiveType: /** @type {any} */ (navigator).connection?.effectiveType,
    });
    if (!allowed) return;

    const strip = this.closest("slideshow-slides");
    if (!strip) return;

    this.#stripObserver = new IntersectionObserver(
      (entries) => {
        this.#inStrip = entries.some((entry) => shown(entry, STRIP_THRESHOLD));
        this.#update();
      },
      { root: strip, threshold: [0, STRIP_THRESHOLD] },
    );
    this.#stripObserver.observe(this);
    getViewportObserver().observe(this);
  }

  disconnectedCallback() {
    // Horizon's infinite slideshow moves the slide node (before(), then replaceWith()) on an
    // arrow click, and Nosto reorders cards: a disconnect immediately followed by a connect.
    // Tearing down on the disconnect would throw away a playing or kept clip each time.
    queueMicrotask(() => {
      if (!this.isConnected) this.#teardown();
    });
  }

  /** @param {boolean} onPage */
  onViewport(onPage) {
    // unobserve() does not drop entries already queued for this element.
    if (!this.isConnected) return;
    this.#onPage = onPage;
    this.#update();
  }

  /** Called by the next card to start. */
  stop() {
    this.#clearDwell();
    if (active === this) active = null;

    const video = this.#video;
    if (!video) return;

    video.pause();
    this.removeAttribute("data-playing");

    const bufferedEnd = video.buffered.length
      ? video.buffered.end(video.buffered.length - 1)
      : 0;
    if (!fullyBuffered({ duration: video.duration, bufferedEnd })) {
      this.#discard();
      return;
    }

    kept = kept.filter((card) => card !== this);
    kept.push(this);
    for (const card of overflow(kept, MAX_KEPT)) card.#discard();
  }

  #update() {
    const isShown = this.#inStrip && this.#onPage;
    const change = transition(this.#shown, isShown);
    this.#shown = isShown;

    if (change === "show") {
      this.#clearDwell();
      this.#dwell = setTimeout(() => {
        this.#dwell = null;
        if (this.isConnected && this.#shown) this.#start();
      }, DWELL_MS);
    } else if (change === "hide") {
      this.#clearDwell();
      if (active === this) this.stop();
    }
  }

  #teardown() {
    this.#stripObserver?.disconnect();
    this.#stripObserver = null;
    viewportObserver?.unobserve(this);
    this.#clearDwell();
    this.#discard();
    this.#inStrip = false;
    this.#onPage = false;
    this.#shown = false;
    if (active === this) setActive(null);
  }

  #start() {
    // Pick from the media's declared sources rather than matching the URL: Shopify names them
    // SD-480p / HD-720p / HD-1080p today, but that is an observed convention, not a contract.
    // The result is rewritten to the storefront's own /cdn/shop/videos/ path so a card and the
    // PDP play from one origin.
    const source = toStorefrontUrl(
      pickRendition(parseSources(this.dataset.media), MAX_HEIGHT),
    );
    if (!source) return;

    if (active && active !== this) active.stop();
    setActive(this);
    kept = kept.filter((card) => card !== this);

    let video = this.#video;
    if (!video) {
      video = document.createElement("video");
      video.muted = true;
      video.defaultMuted = true;
      video.loop = true;
      video.playsInline = true;
      video.preload = "auto";
      video.disablePictureInPicture = true;
      video.setAttribute("disableremoteplayback", "");
      // Decorative: the slide link around it already names the product.
      video.setAttribute("aria-hidden", "true");
      video.addEventListener("playing", () => {
        if (active === this) this.setAttribute("data-playing", "");
      });
      video.src = source;
      this.append(video);
      this.#video = video;
    }

    // An AbortError is our own pause() landing while play() was pending: nothing to undo.
    video.play().catch((error) => {
      if (error?.name === "AbortError" || active !== this) return;
      this.stop();
    });
  }

  #clearDwell() {
    if (this.#dwell) clearTimeout(this.#dwell);
    this.#dwell = null;
  }

  /** Drop the <video>: removing `src` and calling load() cancels its download. */
  #discard() {
    kept = kept.filter((card) => card !== this);
    this.removeAttribute("data-playing");

    const video = this.#video;
    if (!video) return;

    video.pause();
    video.removeAttribute("src");
    video.load();
    video.remove();
    this.#video = null;
  }
}

if (
  typeof customElements !== "undefined" &&
  !customElements.get("card-video")
) {
  customElements.define("card-video", CardVideo);
}
